import { API } from "../../../services";
import { handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";

const basePath = `${API_URL.idp}/userprofile`;

const tenantHeaders = (tenantId, extra = {}) => ({
  headers: {
    ...(tenantId ? { tenantId: String(tenantId).toLowerCase() } : {}),
    ...extra,
  },
});

const getAllTenantUserProfiles = async (tenantId) => {
  return API.get(
    basePath,
    "tenant_userprofiles_list",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const getTenantUserProfileByName = async (profileName, tenantId) => {
  if (!profileName) {
    throw new Error("Profile name is missing.");
  }

  return API.get(
    `${basePath}/${encodeURIComponent(profileName)}`,
    "tenant_userprofile_by_name",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const createTenantUserProfile = async (payload = {}, tenantId) => {
  const profileName = String(payload.profileName || payload.name || "").trim();
  if (!profileName) {
    throw new Error("Profile name is required.");
  }

  const passwordExpiryInSecs = Number(
    payload.passwordExpiryInSecs ?? payload.passwordExpiry
  );
  if (!Number.isFinite(passwordExpiryInSecs) || passwordExpiryInSecs < 0) {
    throw new Error("Password expiry (seconds) is required.");
  }

  // POST body: { profileName, passwordExpiryInSecs } only.
  return API.post(
    basePath,
    "tenant_userprofile_create",
    {
      profileName,
      passwordExpiryInSecs,
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantUserProfile = async (profileName, payload = {}, tenantId) => {
  const name = String(profileName || "").trim();
  if (!name) {
    throw new Error("Profile name is missing.");
  }

  // PUT body: { passwordExpiryInSecs, mfaConfiguration } only.
  // GET uses passwordExpiry / mfaConfig — those names are rejected on update.
  const body = {};
  const expiry = Number(
    payload.passwordExpiryInSecs ?? payload.passwordExpiry
  );
  if (
    payload.passwordExpiryInSecs !== undefined ||
    payload.passwordExpiry !== undefined
  ) {
    if (!Number.isFinite(expiry) || expiry < 0) {
      throw new Error("Password expiry (seconds) is invalid.");
    }
    body.passwordExpiryInSecs = expiry;
  }

  const mfa = payload.mfaConfiguration || payload.mfaConfig || null;
  if (mfa && typeof mfa === "object") {
    const enforcement = String(mfa.enforcement || "").trim();
    const allowed = ["mandatory", "optional", "disabled"];
    if (!allowed.includes(enforcement)) {
      throw new Error(
        'MFA enforcement must be "mandatory", "optional", or "disabled".'
      );
    }
    body.mfaConfiguration = {
      enforcement,
      methods: Array.isArray(mfa.methods)
        ? mfa.methods.filter(Boolean).map(String)
        : [],
    };
  }

  return API.put(
    `${basePath}/${encodeURIComponent(name)}`,
    "tenant_userprofile_update",
    body,
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const deleteTenantUserProfile = async (profileName, tenantId) => {
  if (!profileName) {
    throw new Error("Profile name is missing.");
  }

  return API.delete(
    `${basePath}/${encodeURIComponent(profileName)}`,
    "tenant_userprofile_delete",
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

export const tenantUserProfileService = {
  getAllTenantUserProfiles,
  getTenantUserProfileByName,
  createTenantUserProfile,
  updateTenantUserProfile,
  deleteTenantUserProfile,
};
